<script lang="ts">
  import { tick } from 'svelte'
  import type { OpsPayload } from '../../shared/contract'
  import { items, validBriefing, type Briefing, type ItemState } from '../../shared/history'
  import { Favourites } from './personal.svelte'
  import { useAppearance, type BoardStyle } from './appearance.svelte'
  import { clockTime, dayKey } from './when'

  let { data }: { data: OpsPayload } = $props()
  const favourites = new Favourites()
  const appearance = useAppearance()
  let dialog: HTMLDialogElement
  let input = $state<HTMLInputElement>()
  let trigger: HTMLButtonElement
  let opened = $state(false)
  let mode = $state<'search' | 'favourites' | 'yesterday' | 'style'>('search')
  let query = $state('')
  let favouritesOnly = $state(false)
  let brief = $state.raw<Briefing | null>(null)
  let failed = $state(false)
  let loading = $state(false)
  const rows = $derived(items(data).filter(row => row.item.name.toLowerCase().includes(query.trim().toLowerCase()) || row.area.toLowerCase().includes(query.trim().toLowerCase())))
  const found = $derived(mode === 'favourites' ? rows.filter(row => favourites.has(row.key)) : query.trim() ? rows : [])
  const changes = $derived((brief?.changes ?? []).filter(row => !favouritesOnly || favourites.has(row.key)))
  const stateText = (s: ItemState) => `${s.status || (s.open ? 'open' : 'closed')}${s.groomed ? ' · groomed' : ''}`
  const at = (value: string) => `${dayKey(new Date(value))}, ${clockTime(new Date(value))}`

  $effect(() => {
    if (!opened) return
    void data.checkedAt
    const controller = new AbortController()
    brief = null
    failed = false
    loading = true
    void fetch('/api/briefing.json', { cache: 'no-cache', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]) })
      .then(async response => {
        if (!response.ok) throw new Error('unavailable')
        const value: unknown = await response.json()
        if (!validBriefing(value)) throw new Error('invalid')
        if (!controller.signal.aborted) brief = value
      }).catch(() => { if (!controller.signal.aborted) failed = true })
      .finally(() => { if (!controller.signal.aborted) loading = false })
    return () => controller.abort()
  })
  async function show() {
    mode = favourites.keys.length ? 'favourites' : 'search'
    opened = true
    await tick()
    dialog.showModal()
    input?.focus()
  }
  function close() { dialog.close(); opened = false; trigger?.focus() }
  async function chooseStyle(value: BoardStyle) {
    appearance.choose(value)
    await tick()
    // Switching presentations replaces the header; restore focus to its new tools button.
    document.querySelector<HTMLButtonElement>('[data-mountain-tools]')?.focus()
  }
</script>

<nav class="suite" aria-label="Personal apps">
  <a href="https://wx.trav.gg/">Weather</a>
  <a href="/" aria-current="page">Mountain</a>
  <button bind:this={trigger} data-mountain-tools onclick={show} aria-label="Search, favourites and changes since yesterday" title="Search, favourites, yesterday and style">⌕</button>
</nav>
<dialog bind:this={dialog} onkeydown={(event) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    close()
  }
}} onclose={() => { opened = false; trigger?.focus() }} aria-label="Find your mountain">
  {#if opened}<div class="panel">
    <div class="top"><h2>Find your mountain</h2><button onclick={close} aria-label="Close mountain search">×</button></div>
    <div class="tabs" role="group" aria-label="Mountain view">
      <button aria-pressed={mode === 'search'} onclick={() => mode = 'search'}>Search</button>
      <button aria-pressed={mode === 'favourites'} onclick={() => mode = 'favourites'}>Favourites {favourites.keys.length || ''}</button>
      <button aria-pressed={mode === 'yesterday'} onclick={() => mode = 'yesterday'}>Since yesterday</button>
      <button aria-pressed={mode === 'style'} onclick={() => mode = 'style'}>Style</button>
    </div>
    {#if mode === 'style'}
      <div class="style-choices" role="group" aria-label="Board style">
        <button aria-pressed={appearance.value === 'classic'} onclick={() => chooseStyle('classic')}>
          <strong>Classic</strong><span>The original, understated mountain board.</span>
        </button>
        <button aria-pressed={appearance.value === 'signs'} onclick={() => chooseStyle('signs')}>
          <strong>Trail signs</strong><span>Resort signs, timber posts and drifting snow.</span>
        </button>
      </div>
      <p>Your choice is saved on this device.</p>
    {:else if mode !== 'yesterday'}
      <input bind:this={input} bind:value={query} type="search" placeholder="Lift, run or area" aria-label="Search lifts and runs" />
      <div class="results">
        {#each found as row (row.key)}
          <div class="item">
            <div><strong>{row.item.name}</strong><span>{row.kind} · {row.area} · {stateText(row.item)}</span></div>
            <button aria-label="{favourites.has(row.key) ? 'Unpin' : 'Pin'} {row.item.name}" aria-pressed={favourites.has(row.key)} onclick={() => favourites.toggle(row.key)}>{favourites.has(row.key) ? '★' : '☆'}</button>
          </div>
        {:else}
          <p>{mode === 'favourites' ? 'No matching favourites. Search to pin lifts and runs for this device.' : query.trim() ? 'No lifts or runs match.' : 'Search a name or area, then tap the star to save a favourite.'}</p>
        {/each}
      </div>
    {:else}
      <label class="only"><input type="checkbox" bind:checked={favouritesOnly} /> Favourites only</label>
      <div class="results">
        {#if loading}<p>Reading yesterday’s report…</p>
        {:else if failed || !brief}<p>Yesterday’s comparison is unavailable. Try again when the connection returns.</p>
        {:else if !brief.baseline.available}<p>Yesterday’s baseline is not available yet. Daily reports are being saved; changes will appear after a report has been recorded on the previous Mountain calendar day.</p>
        {:else}
          <p class="provenance">Compared with the last saved report on {brief.baseline.date} (Mountain time){brief.baseline.checkedAt ? ` · checked ${at(brief.baseline.checkedAt)}` : ''}.{brief.current.stale ? ' Current report is cached; these changes may be behind.' : ''}</p>
          {#each changes as change (change.key)}
            <div class="item">
              <div><strong>{change.name}</strong><span>{change.kind} · {change.area}</span><span>{stateText(change.before)} → {stateText(change.after)}</span></div>
              <button aria-label="{favourites.has(change.key) ? 'Unpin' : 'Pin'} {change.name}" aria-pressed={favourites.has(change.key)} onclick={() => favourites.toggle(change.key)}>{favourites.has(change.key) ? '★' : '☆'}</button>
            </div>
          {:else}<p>{favouritesOnly ? 'No recorded changes to your favourites since yesterday.' : 'No recorded lift or run changes since yesterday.'}</p>{/each}
        {/if}
      </div>
    {/if}
    <p class="source">{data.publishedAt ? `Source report updated ${at(data.publishedAt)} · ` : 'Source publication time unavailable · '}checked {at(data.checkedAt)}</p>
  </div>{/if}
</dialog>

<style>
  .suite { display: inline-flex; align-items: center; gap: 10px; margin-left: auto; font-size: 12px; white-space: nowrap; }
  a { color: var(--ink-2); text-decoration: none; display: inline-flex; align-items: center; min-height: 44px; margin-block: -12px; }
  a[aria-current] { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; }
  @media (max-width: 360px) { .suite a[aria-current] { display: none; } }
  .suite button { font-size: 20px; width: 44px; height: 44px; margin-block: -12px; }
  dialog { padding: 0; width: min(620px, calc(100vw - 24px)); max-height: calc(100dvh - 100px); color: var(--ink); background: var(--bg); border: 1px solid var(--glass-edge); border-radius: 20px; box-shadow: 0 18px 70px #0008; }
  dialog::backdrop { background: #0008; backdrop-filter: blur(5px); }
  .panel { display: flex; flex-direction: column; max-height: calc(100dvh - 104px); padding: 14px 18px; gap: 12px; }
  .top { display: flex; align-items: center; justify-content: space-between; }
  h2 { font-size: 20px; }
  .top button { width: 44px; height: 44px; font-size: 26px; }
  .tabs { display: flex; gap: 4px; }
  .tabs button { flex: 1; border-radius: 10px; padding: 10px 4px; min-height: 44px; font-size: 13px; }
  button[aria-pressed='true'] { color: var(--amber-ink); background: var(--lit); }
  input[type='search'] { flex: none; width: 100%; color: var(--ink); background: var(--glass); border: 1px solid var(--glass-edge); border-radius: 12px; padding: 12px; min-height: 44px; font: inherit; }
  .results { overflow-y: auto; min-height: 0; overscroll-behavior: contain; }
  .item { display: flex; align-items: center; justify-content: space-between; gap: 10px; border-bottom: 1px solid var(--line); padding: 8px 0; }
  .item div { display: flex; flex-direction: column; gap: 4px; }
  strong { font-weight: 500; }
  .item span, p { font-size: 12px; color: var(--ink-2); line-height: 1.5; }
  .item button { flex: none; width: 44px; height: 44px; border-radius: 12px; font-size: 23px; }
  .only { display: flex; align-items: center; gap: 8px; min-height: 36px; font-size: 13px; }
  .provenance { margin-bottom: 10px; }
  .source { flex: none; border-top: 1px solid var(--line); padding-top: 8px; }
  .style-choices { display: grid; gap: 8px; }
  .style-choices button { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; min-height: 72px; padding: 12px; border: 1px solid var(--glass-edge); border-radius: 10px; text-align: left; }
  .style-choices strong { font-size: 15px; font-weight: 650; }
  .style-choices span { color: var(--ink-2); font-size: 12px; }
</style>
